import React from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { FlexContainer, TitleH5, Paragraph, CheckMark } from 'heliocor-ui'
import HTMLReactParser from 'html-react-parser';
import isEmpty from 'lodash/isEmpty'

import DocumentViewer from './DocumentViewer'
import { UploadDocument } from './DocumentUpload/UploadDocument'
import { UploadSelfie } from './DocumentUpload/UploadSelfie'
import { Accordion, AccordionProvider } from '../../common'
import { CategoryDescription } from '../../common/Wizard/styles';
import { isUploadAvailable, translateDocTitle } from '../../utils/documents'

const DocumentsSection = ({
    documents, handleUpload, deleteDocument, documentsUploaded = [], owner,
    deleteIndividualDocument, contextDisabled = false, sendForManualValidation, canDelete = true
}) => {
    const { t } = useTranslation()
    const navigation = useSelector(store => store.navigation)
    const isRequest = useSelector(state => state.config.isRequest)
    const selfieMustBeTakenWithCamera = useSelector(state => state.settings.selfieMustBeTakenWithCamera)

    const multiDocuments = ['ID_CARD', 'DRIVING_LICENCE']
    const isStructure = navigation.stepsList[navigation.stepIndex] === 'STRUCTURE'
    const isPhoto = navigation.stepsList[navigation.stepIndex] === 'PHOTO'
    const isSingleDoc = !isStructure && documents.length === 1

    function assignTipValue({ actualDoc }) {
        if (!actualDoc || (actualDoc && (!actualDoc.datas || isEmpty(actualDoc.datas)))) return 'warning.noDocsUploadedYet'
        if (actualDoc.sentForManualValidation || (actualDoc.errors && actualDoc.errors.includes('NOT_VALIDATED'))) return 'warning.docSentForManualValidation'
        if (actualDoc.errors && actualDoc.errors.some(err => err !== 'NOT_VALIDATED')) return 'warning.pleaseReviewDoc'
        if (!actualDoc.errors && !actualDoc.properties) return 'warning.docValidAutomatically'
        if (!actualDoc.errors && !isEmpty(actualDoc.datas)) return 'warning.docSentValidated'
    }

    return (
        <FlexContainer column>
            <AccordionProvider disabled={contextDisabled}>
                {documents && documents
                    .sort((a, b) => a.order - b.order)
                    .map((doc, index) => {
                        const actualDoc = documentsUploaded.find(d => d.type === doc.name)
                        const canUpload = isRequest || isUploadAvailable(doc?.filesNumber, actualDoc?.datas)
                        const actualDocWarning = actualDoc?.errors?.some(reason => ['NOT_VALIDATED'].includes(reason))
                        
                        const documentOptions = doc?.options ? JSON.parse(doc.options) : {}
                        const documentDescription = doc.description || documentOptions?.description 

                        const docHasBeenSubmitted = actualDoc 
                            && !isEmpty(actualDoc.datas) 
                            && (!actualDoc.errors 
                                || actualDocWarning 
                                || actualDoc.sentForManualValidation)

                        const docHasSomeError = actualDoc 
                            && actualDoc.errors
                            && !actualDoc.sentForManualValidation
                            && !isEmpty(actualDoc.datas )
                            && !actualDocWarning 

                        return (
                            <Accordion
                                key={doc.name}
                                isSingleDoc={isSingleDoc}
                                id={`accordion-${doc.name}`}
                                isOpen={isSingleDoc}
                                isFree={isSingleDoc}
                                index={doc.name}
                                lastChild={documents.length - 1 === index}
                                complete={docHasBeenSubmitted}
                                warning={docHasSomeError}
                                titleBox={
                                    <FlexContainer column justifyContent='space-between' alignItems='baseline' height='42px'>
                                        <FlexContainer alignItems='center'>
                                            <TitleH5 value={translateDocTitle(doc)} margin='0 .5rem 0 0' />
                                            {docHasBeenSubmitted ? <CheckMark fill='aqua100' size='16' /> : null}
                                        </FlexContainer>
                                        <Paragraph
                                            textAlign='initial'
                                            margin='0.5rem 0 0'
                                            value={t(assignTipValue({ actualDoc }))}
                                        />
                                    </FlexContainer>
                                }
                                contentBox={
                                    <FlexContainer column minHeight='16rem' data-cy={`accordion-content-${doc.name}`}>
                                        {documentDescription && (
                                            <CategoryDescription padding={"1rem 3rem 0 3rem"}>
                                                {HTMLReactParser(documentDescription)}
                                            </CategoryDescription>
                                        )}

                                        {(actualDoc && !isEmpty(actualDoc.datas))
                                            ? <DocumentViewer
                                                document={actualDoc}
                                                documentConfig={doc}
                                                deleteDocument={deleteDocument}
                                                deleteIndividualDocument={deleteIndividualDocument}
                                                handleUpload={handleUpload}
                                                canUpload={canUpload}
                                                sendForManualValidation={sendForManualValidation}
                                                canDelete={canDelete}
                                                owner={owner}
                                            />
                                            : (['SELFIE', 'VIDEO_SELFIE'].includes(doc.name))
                                                ? <UploadSelfie
                                                    documentConfig={doc}
                                                    handleUpload={handleUpload}
                                                    isCameraNeeded={isStructure || isPhoto}
                                                    selfieMustBeTakenWithCamera={selfieMustBeTakenWithCamera}
                                                />
                                                : <UploadDocument
                                                    documentConfig={doc}
                                                    multiUpload={multiDocuments.includes(doc.name)}
                                                    handleUpload={handleUpload}
                                                    canUpload={canUpload}
                                                />
                                        }
                                    </FlexContainer>
                                }
                            />
                        )
                    })}
            </AccordionProvider>
        </FlexContainer>
    )
}

export {DocumentsSection}